import type { CSSProperties } from 'react';
import { useState } from 'react';
import { ChevronLeftIcon } from '../../icons/ChevronLeftIcon';
import { ChevronRightIcon } from '../../icons/ChevronRightIcon';
import { BackButton } from '../BackButton';
import { Button } from '../Button';
import {
  BadgeContainer,
  Controls,
  DefaultBadge,
  Dot,
  Dots,
  ImageContainer,
  Img,
  NavGroup,
  Outer,
  OuterSticky,
  Overlay,
  Placeholder,
  Slide,
  Track,
} from './ImageGallery.styles';
import type { ImageGalleryProps } from './ImageGallery.types';

function ImageGalleryBase({
  images,
  onBack,
  badge,
  sticky = true,
  prevLabel = 'Previous image',
  nextLabel = 'Next image',
  goToImageLabel = (position, total) => `View image ${position} of ${total}`,
}: ImageGalleryProps) {
  const validImages = images.filter((image) => image.src);
  const [activeIndex, setActiveIndex] = useState(0);

  const WrapperEl = sticky ? OuterSticky : Outer;
  const safeIndex = Math.min(activeIndex, Math.max(validImages.length - 1, 0));
  const hasImages = validImages.length > 0;
  const hasMultiple = validImages.length > 1;

  const goTo = (index: number) => {
    const count = validImages.length;
    setActiveIndex((index + count) % count);
  };

  return (
    <WrapperEl>
      <ImageContainer>
        {hasImages ? (
          <Track
            style={{ '--gallery-index': safeIndex } as CSSProperties}
            data-active-index={safeIndex}
          >
            {validImages.map((image, index) => (
              <Slide key={image.src ?? index} aria-hidden={index !== safeIndex}>
                <Img src={image.src ?? undefined} alt={image.alt ?? ''} />
              </Slide>
            ))}
          </Track>
        ) : (
          <Placeholder />
        )}

        {onBack && (
          <Overlay>
            <BackButton onClick={onBack} variant="overlay" />
          </Overlay>
        )}

        {hasMultiple && (
          <Controls>
            <Dots>
              {validImages.map((image, index) => (
                <Dot
                  key={image.src ?? index}
                  type="button"
                  data-active={index === safeIndex}
                  aria-label={goToImageLabel(index + 1, validImages.length)}
                  aria-current={index === safeIndex}
                  onClick={() => goTo(index)}
                />
              ))}
            </Dots>
            <NavGroup>
              <Button
                variant="icon-ghost"
                aria-label={prevLabel}
                onClick={() => goTo(safeIndex - 1)}
              >
                <ChevronLeftIcon size={20} />
              </Button>
              <Button
                variant="icon-ghost"
                aria-label={nextLabel}
                onClick={() => goTo(safeIndex + 1)}
              >
                <ChevronRightIcon size={20} />
              </Button>
            </NavGroup>
          </Controls>
        )}

        {badge && <BadgeContainer>{badge}</BadgeContainer>}
      </ImageContainer>
    </WrapperEl>
  );
}

export const ImageGallery = Object.assign(ImageGalleryBase, {
  Badge: DefaultBadge,
});
